import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, hasRole } from "@/context/AuthContext";
import { toast } from "sonner";
import { Bot, Send, Loader2, Sparkles, User, Calculator, Camera, X, ArrowRight } from "lucide-react";

const SUGGESTIONS = [
  "Which buyer ordered the most this quarter?",
  "What is my total pending freight to pay?",
  "Which stock items are below minimum?",
  "Total expenses this month by category",
  "How many purchase orders are still in draft?",
];

function renderRich(text) {
  return String(text || "").split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
    seg.startsWith("**") && seg.endsWith("**")
      ? <strong key={i}>{seg.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{seg}</React.Fragment>
  );
}

export default function Assistant() {
  const { user } = useAuth();
  const canCost = hasRole(user, "admin", "manager");
  const navigate = useNavigate();
  const [turns, setTurns] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  // Costing agent
  const [photos, setPhotos] = useState([]);       // [{ file, url }]
  const [cForm, setCForm] = useState({ product_name: "", category: "", wood_type: "", overall_size: "" });
  const [cBusy, setCBusy] = useState(false);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, loading]);

  const addPhotos = (fileList) => {
    const incoming = [...(fileList || [])].map((f) => ({ file: f, url: URL.createObjectURL(f) }));
    setPhotos((p) => [...p, ...incoming].slice(0, 3));
  };
  const runCosting = async () => {
    if (!photos.length || cBusy) return;
    setCBusy(true);
    try {
      let photoPath = "";
      try { const up = new FormData(); up.append("file", photos[0].file); const r = await api.post("/upload", up); photoPath = r.data?.path || ""; } catch (e) { console.warn("Cover photo upload failed", e); }
      const fd = new FormData();
      photos.forEach((p) => fd.append("files", p.file));
      fd.append("product_name", cForm.product_name);
      fd.append("category", cForm.category);
      fd.append("wood_type", cForm.wood_type);
      fd.append("overall_size", cForm.overall_size);
      fd.append("photo_path", photoPath);
      const { data } = await api.post("/costings/from-photo", fd);
      toast.success(`Draft cost sheet ${data?.costing?.sku || ""} created — review & edit`);
      navigate("/costing");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Costing agent failed");
    } finally { setCBusy(false); }
  };

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (!text || loading) return;
    setQ("");
    const history = turns.slice(-4);
    setTurns((t) => [...t, { q: text, a: null }]);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/ask", { question: text, history });
      setTurns((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, a: data.answer } : x)));
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Assistant unavailable";
      setTurns((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, a: `⚠ ${msg}` } : x)));
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="AI Assistant" subtitle="Ask about your orders, buyers, stock, freight & finances — answered from your live data." icon={Bot} />

      {canCost && (
        <Card className="rounded-md border-primary/30 bg-primary/5 p-5 shadow-none" data-testid="costing-agent">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-primary"><Calculator className="h-4.5 w-4.5" /></div>
            <div>
              <p className="font-heading text-sm font-bold">Costing Agent</p>
              <p className="text-xs text-muted-foreground">Upload a product photo — AI drafts the full cost sheet (BOM, materials, labour) for you to review.</p>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-[210px,1fr]">
            <div>
              <div className="grid grid-cols-3 gap-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative" data-testid={`costing-preview-${i}`}>
                    <img src={p.url} alt="" className="h-16 w-full rounded border border-border object-cover" />
                    <button type="button" onClick={() => setPhotos(photos.filter((_, x) => x !== i))} className="absolute -right-1 -top-1 rounded-full border border-border bg-background p-0.5"><X className="h-3 w-3" /></button>
                  </div>
                ))}
                {photos.length < 3 && (
                  <label className="flex h-16 cursor-pointer flex-col items-center justify-center rounded border border-dashed border-primary/50 text-primary hover:bg-primary/5" data-testid="costing-photo-label">
                    <Camera className="h-4 w-4" /><span className="text-[9px]">Add</span>
                    <input type="file" accept="image/*" capture="environment" multiple className="hidden" data-testid="costing-photo-upload" onChange={(e) => addPhotos(e.target.files)} />
                  </label>
                )}
              </div>
              <p className="mt-1.5 text-[10px] text-muted-foreground">Up to 3 angles (front / back / detail) for a sharper estimate.</p>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1"><Label className="text-xs">Product name</Label>
                  <Input data-testid="costing-name" value={cForm.product_name} onChange={(e) => setCForm({ ...cForm, product_name: e.target.value })} placeholder="Sheesham King Bed" /></div>
                <div className="space-y-1"><Label className="text-xs">Category</Label>
                  <Input data-testid="costing-category" value={cForm.category} onChange={(e) => setCForm({ ...cForm, category: e.target.value })} placeholder="Bed / Dining…" /></div>
                <div className="space-y-1"><Label className="text-xs">Wood</Label>
                  <Input data-testid="costing-wood" value={cForm.wood_type} onChange={(e) => setCForm({ ...cForm, wood_type: e.target.value })} placeholder="Sheesham" /></div>
                <div className="space-y-1"><Label className="text-xs">Dimensions (optional)</Label>
                  <Input data-testid="costing-size" value={cForm.overall_size} onChange={(e) => setCForm({ ...cForm, overall_size: e.target.value })} placeholder="72x36x40 in" /></div>
              </div>
              <div className="flex items-center gap-2">
                <Button data-testid="costing-generate" onClick={runCosting} disabled={!photos.length || cBusy} className="gap-2">
                  {cBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Analysing…</> : <><Sparkles className="h-4 w-4" /> Generate cost sheet</>}
                </Button>
                {cBusy && <span className="text-xs text-muted-foreground">Estimating BOM & rates — ~15s.</span>}
              </div>
              <p className="text-[11px] text-muted-foreground">Creates an editable <b>draft cost sheet</b>. Rates auto-fill from your Rate Sheets where matched; amber lines in Costing are <b>AI estimates to verify</b>.</p>
            </div>
          </div>
        </Card>
      )}

      <Card className="flex h-[calc(100vh-15rem)] flex-col overflow-hidden rounded-md border-border shadow-none">
        <div className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="assistant-thread">
          {turns.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"><Sparkles className="h-7 w-7" /></div>
              <p className="mt-4 font-heading text-lg font-bold">Ask me about your business</p>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">I read your orders, stock, freight and finances to answer in plain English.</p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid="assistant-suggestion" onClick={() => ask(s)}
                    className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary">{s}</button>
                ))}
              </div>
            </div>
          )}
          {turns.map((t, i) => (
            <div key={i} className="space-y-3">
              <div className="flex justify-end">
                <div className="flex max-w-[80%] items-start gap-2">
                  <div className="rounded-2xl rounded-tr-sm bg-primary px-4 py-2 text-sm text-primary-foreground" data-testid="assistant-question">{t.q}</div>
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"><User className="h-4 w-4" /></div>
                </div>
              </div>
              <div className="flex justify-start">
                <div className="flex max-w-[85%] items-start gap-2">
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Bot className="h-4 w-4" /></div>
                  {t.a === null
                    ? <div className="flex items-center gap-2 rounded-2xl rounded-tl-sm bg-muted px-4 py-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Thinking…</div>
                    : <div className="whitespace-pre-wrap rounded-2xl rounded-tl-sm bg-muted px-4 py-2 text-sm" data-testid="assistant-answer">{renderRich(t.a)}</div>}
                </div>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="border-t border-border p-3">
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex items-center gap-2">
            <Input data-testid="assistant-input" value={q} onChange={(e) => setQ(e.target.value)} disabled={loading}
              placeholder="Ask about orders, buyers, stock, freight…" className="flex-1" />
            <Button type="submit" data-testid="assistant-send" disabled={loading || !q.trim()} className="gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
